import React, { useState } from "react";
import { Search, Store, HandCoins, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { supportWaLink, SUPPORT_WHATSAPP } from "@/lib/api";

const CONTENT = {
  how: {
    title: "كيف تعمل مخزون كار؟",
    body: (
      <div className="grid gap-6 md:grid-cols-3 mt-8">
        {[
          { icon: Search, t: "١. ابحث عن القطعة", d: "اكتب اسم القطعة أو رقمها، أو اختر سيارتك وسنة الصنع، وابحث بين تشاليح مدينتك في ثوانٍ." },
          { icon: Store, t: "٢. قارن وتواصل", d: "قارن الأسعار وحالة القطع والتقييمات بين التشاليح المعتمدة، وتواصل مع البائع مباشرة." },
          { icon: HandCoins, t: "٣. اطلب وادفع عند الاستلام", d: "أرسل طلبك، يجهّزه التشليح، تستلم قطعتك وتدفع قيمتها مباشرة للبائع. بدون أي عمولة من المنصة." },
        ].map((s, i) => (
          <div key={i} className="ts-card p-6 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] mb-4"><s.icon size={30} /></span>
            <h3 className="font-bold text-white text-lg mb-2">{s.t}</h3>
            <p className="text-sm text-[var(--text-muted)] leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    ),
  },
  privacy: {
    title: "سياسة الخصوصية",
    body: (
      <div className="prose-invert mt-6 space-y-4 text-[var(--text-muted)] leading-relaxed text-sm">
        <p>نلتزم في «مخزون كار» بحماية خصوصية مستخدمينا. يتم جمع البيانات الضرورية فقط لتقديم الخدمة مثل الاسم ورقم الجوال والبريد الإلكتروني وبيانات الطلبات.</p>
        <p>لا تتم مشاركة بياناتك مع أي طرف ثالث دون موافقتك، عدا ما يلزم لإتمام الطلب مع التشليح المعني.</p>
        <p>رقم السجل التجاري للتشاليح محفوظ بشكل آمن ولا يظهر كاملًا للعملاء علنًا.</p>
        <p>يتم تخزين كلمات المرور بشكل مشفّر، ولا يمكن لأي جهة الاطلاع عليها.</p>
      </div>
    ),
  },
  terms: {
    title: "الشروط والأحكام",
    body: (
      <div className="mt-6 space-y-4 text-[var(--text-muted)] leading-relaxed text-sm">
        <p>باستخدامك منصة «مخزون كار» فإنك توافق على أن المنصة وسيط يربط العملاء بالتشاليح، ولا تتحمل مسؤولية جودة القطع التي يوفرها البائعون.</p>
        <p>لا تأخذ المنصة أي عمولة أو نسبة من قيمة بيع قطع الغيار — قيمة القطعة كاملة تعود للتشليح / البائع.</p>
        <p>في المرحلة الحالية طريقة الدفع المتاحة هي الدفع عند الاستلام، ويتم الدفع مباشرة من العميل للتشليح.</p>
        <p>يلتزم التشليح بصحة بياناته ورقم سجله التجاري، وللإدارة الحق في رفض أو إيقاف أي حساب مخالف.</p>
      </div>
    ),
  },
};

export function StaticPage({ type }) {
  if (type === "contact") return <ContactPage />;
  const c = CONTENT[type];
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-12">
      <h1 className="font-head text-3xl font-black text-white text-center">{c.title}</h1>
      {c.body}
    </div>
  );
}

function ContactPage() {
  const [f, setF] = useState({ name: "", email: "", msg: "" });
  const submit = (e) => { e.preventDefault(); toast.success("تم استلام رسالتك، سنتواصل معك قريبًا"); setF({ name: "", email: "", msg: "" }); };
  return (
    <div className="mx-auto max-w-lg px-4 py-12">
      <div className="text-center mb-8">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] mb-3"><Mail size={28} /></span>
        <h1 className="font-head text-3xl font-black text-white">تواصل معنا</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">نسعد بخدمتك واستقبال ملاحظاتك</p>
      </div>
      <form onSubmit={submit} className="ts-card p-6 space-y-4">
        <input required placeholder="الاسم" data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none" />
        <input required type="email" placeholder="البريد الإلكتروني" data-testid="contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none" />
        <textarea required placeholder="رسالتك" rows={4} data-testid="contact-msg" value={f.msg} onChange={(e) => setF({ ...f, msg: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none resize-none" />
        <button data-testid="contact-submit" className="btn-amber w-full justify-center"><Send size={16} /> إرسال</button>
      </form>
      <div className="ts-card p-5 mt-4 text-center">
        <p className="text-sm text-[var(--text-muted)] mb-3">للمساعدة الفورية تواصل مع دعم المنصة عبر واتساب</p>
        <a data-testid="contact-support-whatsapp" href={supportWaLink()} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-3 font-bold text-white hover:bg-[#1ebe57] transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18c-1.5 0-3-.4-4.2-1.2l-.3-.2-3.1.8.8-3-.2-.3C4.4 15 4 13.5 4 12c0-4.4 3.6-8 8-8s8 3.6 8 8-3.6 8-8 8zm5.5-5.6c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.6.2-.2.3-.7.9-.8 1-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.6-1.5-.9-2c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"/></svg>
          دعم واتساب · {SUPPORT_WHATSAPP}
        </a>
      </div>
    </div>
  );
}
